/**
 * DimensionGrid Component
 *
 * Responsive grid layout for all 12 dimension cards.
 *
 * ⛔ THE COUNT IN THIS COMMENT IS LOAD-BEARING AND WAS WRONG FOR A WHOLE PR. The backend gained a
 * 12th dimension (attributeValueRanges) while every list here stayed at eleven, so the dimension
 * was INVISIBLE unless it raised a blocker — and then it rendered with its RAW KEY as the label.
 * A dimension that can only ever appear as bad news is useless for the one thing a readiness
 * dashboard is for: showing progress. NO BUTTON = NOT DELIVERED (standing directive, 2026-08-02).
 * Groups dimensions into logical categories with section headers.
 */

import { useState } from 'react';
import { DimensionCard } from './DimensionCard';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';

interface DimensionAction {
  label: string;
  url: string | null;
  inlineAction?: string;
}

interface DimensionData {
  score: number;
  status: 'green' | 'yellow' | 'red';
  metric: string;
  action: DimensionAction;
  [key: string]: unknown;
}

interface Dimensions {
  productsUploaded: DimensionData;
  productsCategorized: DimensionData;
  categoriesResearched: DimensionData;
  productsEnriched: DimensionData;
  suspiciousValues: DimensionData;
  categoryDemand: DimensionData;
  buyerPersonas: DimensionData;
  perceivedBenefits: DimensionData;
  embeddingsDone: DimensionData;
  questionWheels: DimensionData;
  aeoGenerated: DimensionData;
  attributeValueRanges: DimensionData;
}

interface DimensionGridProps {
  dimensions: Dimensions;
  siteId: string;
  onRefresh: () => void;
}

// Group dimensions for better UX
const dimensionGroups = [
  {
    title: 'Data Foundation',
    dimensions: ['productsUploaded', 'productsCategorized', 'categoriesResearched', 'productsEnriched', 'suspiciousValues']
  },
  {
    title: 'Context & Intelligence',
    dimensions: ['categoryDemand', 'buyerPersonas', 'perceivedBenefits', 'attributeValueRanges', 'embeddingsDone', 'questionWheels', 'aeoGenerated']
  }
];

const dimensionTitles: Record<string, string> = {
  productsUploaded: 'Products Uploaded',
  productsCategorized: 'Products Categorized',
  categoriesResearched: 'Categories Researched',
  productsEnriched: 'Products AI Enriched',
  suspiciousValues: 'Quality Issues',
  categoryDemand: 'Category Demand',
  buyerPersonas: 'Buyer Personas',
  perceivedBenefits: 'Perceived Benefits',
  embeddingsDone: 'Embeddings Done',
  questionWheels: 'Question Wheels',
  aeoGenerated: 'AEO Generated',
  attributeValueRanges: 'Attribute Value Ranges'
};

export function DimensionGrid({ dimensions, siteId, onRefresh }: DimensionGridProps) {
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const { toast } = useToast();

  const handleInlineAction = async (action: string) => {
    setLoadingAction(action);

    try {
      if (action === 'generateEmbeddings') {
        const response = await authFetch(`/api/aeo-readiness/site/${siteId}/action/generate-embeddings`, {
          method: 'POST'
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to trigger embedding generation');
        }

        const result = await response.json();

        toast({
          title: 'Embeddings Generation Started',
          description: result.data.message,
        });

        // Refresh after a short delay
        setTimeout(onRefresh, 2000);
      }
    } catch (error) {
      console.error('Inline action error:', error);
      toast({
        title: 'Action Failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive'
      });
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <div className="space-y-6">
      {dimensionGroups.map((group) => (
        <div key={group.title}>
          <h3 className="text-sm font-medium text-muted-foreground mb-3">
            {group.title}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {group.dimensions.map((key) => {
              const dimension = dimensions[key as keyof Dimensions];
              if (!dimension) return null;

              return (
                <DimensionCard
                  key={key}
                  title={dimensionTitles[key] || key}
                  dimensionKey={key}
                  data={dimension}
                  onInlineAction={handleInlineAction}
                  isActionLoading={loadingAction === dimension.action.inlineAction}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
